<script lang="ts">
	import { ControlsContainer, Slider, SliderV2 } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { real_id } from 'src/helpers/interface_helpers';
	import { resolveXT1ComponentChannel } from 'src/helpers/xt1_helpers/template_helpers';
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Components
	import ControlOnOff from '../../Controls/Single/ControlOnOff.svelte';
	import ControlDirectionDevicePageG2 from '../../Controls/Single/ControlDirectionDevicePageG2.svelte';
	import ControlDirectionDevicePageG2V2 from '../../Controls/Single/ControlDirectionDevicePageG2V2.svelte';
	import ControlPositionG2 from '../../Controls/Single/ControlPositionG2.svelte';
	import ControlPositionG2V2 from '../../Controls/Single/ControlPositionG2V2.svelte';
	import InputsReaderVisualizer from '../../Controls/Indicators/InputsReaderVisualizer.svelte';
	import InputsReaderVisualizerV2 from '../../Controls/Indicators/InputsReaderVisualizerV2.svelte';
	//Types
	import type { Device } from 'src/types/Device';

	export let device: Device;
	export let useV2Components = false;

	let isLoading = false;
	let optimisticBrightness: number | null = null;

	$: component = device?.component || '';
	$: deviceId = real_id(device?.id);
	$: status = $devices_status[deviceId];
	$: settings = $devices_settings[deviceId];
	$: componentType = component.split(':')?.[0];
	$: isLightComponent = /^(light|cct|rgb|rgbw)$/.test(componentType);
	$: brightness = optimisticBrightness ?? getBrightnessValue(status, component);
	$: SliderComponent = useV2Components ? SliderV2 : Slider;

	const a11yProps = {
		ariaLabel: $format('control_slider', { default: 'Control slider' }),
		ariaLabelValue: $format('control_slider', { default: 'Control slider' }),
		sliderAriaLabel: $format('control_slider', { default: 'Control slider' }),
	};

	const getBrightnessValue = (status: any, component: string) => {
		const value = status?.[component]?.brightness ?? 0;
		return Number.isFinite(Number(value)) ? Number(value) : 0;
	};

	const changeBrightness = async (value: number) => {
		const brightness = Number(value);
		if (!Number.isFinite(brightness)) return;

		isLoading = true;
		optimisticBrightness = brightness;

		try {
			const componentIndex = Number(component.split(':')?.[1] ?? device?.channel ?? 0);
			const res = await gen2Request(device, `${componentType}.set`, {
				id: componentIndex,
				brightness,
			});

			if (res?.isok) {
				devices_status.update_device_status({
					id: deviceId,
					msg: {
						...status,
						[component]: {
							...status?.[component],
							brightness,
						},
					},
				});
			}
		} finally {
			optimisticBrightness = null;
			isLoading = false;
		}
	};
</script>

{#if /^(relay|light)$/.test(device?.category)}
	<div class="flex-full-height justify-content-end">
		{#if isLightComponent}
			<svelte:component
				this={SliderComponent}
				id="slider-xt1-brightness"
				class={useV2Components
					? 'flex-full-height justify-content-center mb-s'
					: 'flex-full-height mb-l'}
				title={capFirstChar($format('label_brightness', { default: 'brightness' }))}
				value={brightness}
				type="vertical"
				min={0}
				max={100}
				step={1}
				{a11yProps}
				on:value_change={(e) => changeBrightness(e.detail?.value)}
				{isLoading}
			/>
		{/if}
		<ControlOnOff {device} {status} {settings} isWide {useV2Components} />
	</div>
{:else if device?.category === 'inputs_reader'}
	<div class="flex-full-height justify-content-end">
		<ControlsContainer color={useV2Components ? 'color-0' : undefined}>
			<svelte:fragment slot="first">
				{#if useV2Components}
					<InputsReaderVisualizerV2 {device} {status} {settings} isInCard={false} />
				{:else}
					<InputsReaderVisualizer {device} {status} {settings} isWide isInCard={false} />
				{/if}
			</svelte:fragment>
		</ControlsContainer>
	</div>
{:else if device?.category === 'roller' || componentType === 'cover'}
	{@const coverStatus = status?.[component]}
	{@const controlDevice = resolveXT1ComponentChannel(device)}
	<div class="flex-full-height justify-content-end">
		{#if coverStatus?.pos_control && typeof coverStatus?.current_pos === 'number'}
			{#if useV2Components}
				<ControlPositionG2V2 device={controlDevice} {status} {settings} />
			{:else}
				<ControlPositionG2 device={controlDevice} {status} {settings} />
			{/if}
		{/if}
		{#if useV2Components}
			<ControlDirectionDevicePageG2V2 device={controlDevice} {status} />
		{:else}
			<ControlDirectionDevicePageG2 device={controlDevice} {status} />
		{/if}
	</div>
{/if}
